// MARK://dhk8/admin-shell/pages/modules/LoyaltyConfigPage
// Configuración del programa de lealtad por tenant — on/off + niveles (tiers).
// El negocio define sus propios niveles (nombre + umbral de puntos). El nivel
// de un cliente se deriva de sus puntos contra los umbrales.
// Fuente: dhk8-crm-contacts → GET/PUT /api/loyalty/config.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Trash2, Star, ArrowUpDown } from 'lucide-react'
import { getLoyaltyConfig, saveLoyaltyConfig, type LoyaltyConfig } from '../../lib/clients/customers.js'

interface DraftTier { _k: string; name: string; threshold: string; color: string }

let keySeq = 0
const nk = () => `t${++keySeq}`

const TIER_PRESETS = [
  { name: 'Bronce', threshold: '0', color: '#cd7f32' },
  { name: 'Plata', threshold: '500', color: '#9ca3af' },
  { name: 'Oro', threshold: '2000', color: '#d4af37' },
]

export default function LoyaltyConfigPage() {
  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [enabled, setEnabled] = useState(true)
  const [pointsPerVisit, setPointsPerVisit] = useState('10')
  const [tiers, setTiers] = useState<DraftTier[]>([])
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState<string | null>(null)
  const [savedOk, setSavedOk] = useState(false)

  function hydrate(c: LoyaltyConfig) {
    setEnabled(c.enabled)
    setPointsPerVisit(String(c.pointsPerVisit))
    setTiers(c.tiers.map((t) => ({ _k: nk(), name: t.name, threshold: String(t.threshold), color: t.color ?? '#9ca3af' })))
  }

  useEffect(() => {
    let alive = true
    getLoyaltyConfig()
      .then((c) => { if (alive) { hydrate(c); setLoading(false) } })
      .catch((e) => { if (alive) { setLoadErr(e instanceof Error ? e.message : 'No se pudo cargar.'); setLoading(false) } })
    return () => { alive = false }
  }, [])

  function addTier() {
    setTiers([...tiers, { _k: nk(), name: '', threshold: '0', color: '#9ca3af' }])
  }
  function loadPreset() {
    setTiers(TIER_PRESETS.map((p) => ({ _k: nk(), name: p.name, threshold: p.threshold, color: p.color })))
  }
  function patchTier(k: string, patch: Partial<DraftTier>) {
    setTiers(tiers.map((t) => (t._k === k ? { ...t, ...patch } : t)))
  }

  async function save() {
    setSavedOk(false)
    const parsedTiers = tiers.map((t) => ({
      name: t.name.trim(),
      threshold: Math.max(0, Math.round(Number(t.threshold) || 0)),
      color: t.color || null,
    }))
    for (const t of parsedTiers) {
      if (!t.name) { setSaveErr('Todo nivel necesita un nombre.'); return }
    }
    const thresholds = parsedTiers.map((t) => t.threshold)
    if (new Set(thresholds).size !== thresholds.length) {
      setSaveErr('No puede haber dos niveles con el mismo umbral de puntos.')
      return
    }
    setSaving(true)
    setSaveErr(null)
    try {
      const next = await saveLoyaltyConfig({
        enabled,
        pointsPerVisit: Math.max(0, Math.round(Number(pointsPerVisit) || 0)),
        tiers: parsedTiers,
      })
      hydrate(next)
      setSavedOk(true)
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : 'No se pudo guardar.')
    } finally {
      setSaving(false)
    }
  }

  // Vista previa ordenada — así ve el negocio cómo escalan los niveles.
  const sortedTiers = [...tiers].sort((a, b) => Number(a.threshold) - Number(b.threshold))

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Programa de lealtad</h1>
          <p className="adm-page__sub">Enciende la lealtad y define los niveles de tus clientes</p>
        </div>
        <div className="adm-page__actions">
          <Link to="/customers" className="adm-btn adm-btn--sm">← Clientes</Link>
        </div>
      </div>

      {loading && <div className="adm-card">Cargando…</div>}
      {loadErr && <div className="adm-banner adm-banner--danger">{loadErr}</div>}

      {!loading && !loadErr && (
        <>
          {/* ── On / off ── */}
          <div className="adm-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Star size={16} aria-hidden /> Lealtad {enabled ? 'activada' : 'desactivada'}
              </div>
              <p className="adm-page__sub" style={{ margin: '2px 0 0' }}>
                {enabled
                  ? 'Los clientes acumulan puntos y se les asigna un nivel.'
                  : 'La lealtad está apagada — no se muestran puntos ni niveles.'}
              </p>
            </div>
            <button
              type="button"
              className={enabled ? 'adm-btn adm-btn--primary' : 'adm-btn'}
              onClick={() => setEnabled(!enabled)}
              aria-pressed={enabled}
            >
              {enabled ? 'Apagar lealtad' : 'Encender lealtad'}
            </button>
          </div>

          {/* ── Puntos por visita ── */}
          <div className="adm-card">
            <label className="adm-field" style={{ maxWidth: 240 }}>
              <span className="adm-field__label">Puntos por visita</span>
              <input
                className="adm-input"
                type="number"
                min={0}
                value={pointsPerVisit}
                onChange={(e) => setPointsPerVisit(e.target.value)}
                disabled={!enabled}
              />
            </label>
            <p className="adm-page__sub" style={{ margin: '8px 0 0' }}>
              Puntos por defecto que se otorgan cuando un cliente registra una visita.
            </p>
          </div>

          {/* ── Niveles ── */}
          <div className="adm-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <h3 className="adm-card__title" style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <ArrowUpDown size={15} aria-hidden /> Niveles
              </h3>
              <div style={{ display: 'flex', gap: 6 }}>
                {tiers.length === 0 && (
                  <button type="button" className="adm-btn adm-btn--sm" onClick={loadPreset} disabled={!enabled}>
                    Usar Bronce / Plata / Oro
                  </button>
                )}
                <button type="button" className="adm-btn adm-btn--sm" onClick={addTier} disabled={!enabled}>
                  <Plus size={14} aria-hidden /> Agregar nivel
                </button>
              </div>
            </div>

            {tiers.length === 0 && (
              <div className="adm-empty" style={{ padding: 24 }}>
                Aún no hay niveles. Define al menos uno o usa el preset.
              </div>
            )}

            {tiers.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {tiers.map((t) => (
                  <div key={t._k} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <label className="adm-field" style={{ flex: '2 1 140px' }}>
                      <span className="adm-field__label">Nombre del nivel</span>
                      <input
                        className="adm-input"
                        value={t.name}
                        placeholder="ej. Oro"
                        disabled={!enabled}
                        onChange={(e) => patchTier(t._k, { name: e.target.value })}
                      />
                    </label>
                    <label className="adm-field" style={{ flex: '1 1 120px' }}>
                      <span className="adm-field__label">Umbral de puntos</span>
                      <input
                        className="adm-input"
                        type="number"
                        min={0}
                        value={t.threshold}
                        disabled={!enabled}
                        onChange={(e) => patchTier(t._k, { threshold: e.target.value })}
                      />
                    </label>
                    <label className="adm-field" style={{ flex: '0 0 64px' }}>
                      <span className="adm-field__label">Color</span>
                      <input
                        className="adm-input"
                        type="color"
                        value={t.color}
                        disabled={!enabled}
                        style={{ padding: 2, height: 44 }}
                        onChange={(e) => patchTier(t._k, { color: e.target.value })}
                      />
                    </label>
                    <button
                      type="button"
                      className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger"
                      aria-label="Quitar nivel"
                      onClick={() => setTiers(tiers.filter((x) => x._k !== t._k))}
                    >
                      <Trash2 size={15} aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {sortedTiers.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <span className="adm-field__label">Vista previa:</span>
                {sortedTiers.map((t) => (
                  <span
                    key={t._k}
                    className="adm-badge"
                    style={{ background: `${t.color}22`, color: t.color }}
                  >
                    {t.name || '(sin nombre)'} · {t.threshold} pts
                  </span>
                ))}
              </div>
            )}
          </div>

          {saveErr && <div className="adm-banner adm-banner--danger">{saveErr}</div>}
          {savedOk && <div className="adm-banner">Configuración de lealtad guardada.</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="adm-btn adm-btn--primary" onClick={save} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar configuración'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
